/**
 * Hub feature permission keys — the `permissions` claim of an `aud: morgens-hub` token
 * carries a subset of these. Convention: `<resource>.<action>` (`manage` = create + edit).
 *
 * SOURCE OF TRUTH: Odin. The catalogue (key + bilingual `label` + module `group` + `position` +
 * the three tier roles) lives in the platform's versioned manifest — Odin repo
 * `scripts/manifests/morgens-hub.json` — and is loaded by the declarative desired-state sync
 * (`PUT /v1/admin/apps/morgens-hub/rbac`). This module only mirrors the VOCABULARY so the BFF
 * guards and the web menu never drift from it. Adding a key here does NOT create it in Odin.
 * Platform reference: az Odin-repó `docs/consumer-apps.md` §2 (Hub) (the successor of the older
 * `knowledge-hub-and-ai-assistant.md`) + `iam-security-model.md` §4–§6.
 *
 * Display labels (bilingual) and module grouping live in Odin too
 * (`GET /v1/admin/apps/morgens-hub/permissions`) — never hardcode a label dictionary here.
 *
 * RE-VERIFIED against the manifest (2026-08-10): the catalogue holds **17** rows in `position`
 * order 0–16 under **five** module groups (`access`, `content`, `structure`, `collaboration`,
 * `editorial`). Row 0 is the SCOPE-formatted `knowledge:read` (see `KNOWLEDGE_READ_SCOPE`), rows
 * 1–16 are the 16 feature keys below. Tier defaults: member 3, admin 13, superadmin 17 — each
 * including the scope key. (The earlier "16 keys / positions 0–15 / four groups / member 2, admin
 * 12, superadmin 16" note was measured on the 2026-07-30 dump and is now superseded.)
 *
 * The catalogue calls `space` a "section" (hu: Szekció) and `category` a "book" (hu: Könyv) — the
 * Hub UI follows that wording; these identifiers do not change.
 */
export const PERMISSIONS = {
    // Cikkek
    articleView: 'article.view',
    articleManage: 'article.manage',
    articlePublish: 'article.publish',
    articleUnpublish: 'article.unpublish',
    articleArchive: 'article.archive',
    articleDelete: 'article.delete',
    articlePublishPublic: 'article.publish_public',
    // Szerkezet
    spaceManage: 'space.manage',
    spaceDelete: 'space.delete',
    categoryManage: 'category.manage',
    categoryDelete: 'category.delete',
    // Együttműködés
    commentAdd: 'comment.add',
    commentModerate: 'comment.moderate',
    // Szerkesztőségi
    revisionRestore: 'revision.restore',
    gapReview: 'gap.review',
    knowledgeAdmin: 'knowledge.admin',
    // Sablonok — a cikk-vázak és a beszúrható részletek karbantartása (2026-08-27).
    templateManage: 'template.manage',
};
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
/**
 * `knowledge:read` — SCOPE-formátumú kulcs, ami az Odin permission-KATALÓGUSÁBAN is szerepel
 * (position 0, `access` csoport), ezért a token `permissions` claimjében is megjelenik: a claim a
 * `scope` **supersetje**, nem a komplementere (`iam-security-model.md` §4/§6.3).
 *
 * Miért NEM a `PERMISSIONS` objektumban: az ott felsorolt kulcsok a BFF-guardok tárgyai
 * (`@RequirePermissions(...)`). Ez viszont az app **Odin-adathozzáférését** szabályozza (a busz
 * scope-guardja dönt róla), nem azt, hogy a user mit tehet a Hubban — guard-tárgyként használva
 * hamis biztonságérzetet adna. A tier-tükör mégis tartalmazza, mert az Odin szerepei is: a
 * szimulátornak és a teszteknek ugyanazt a claim-halmazt kell adnia, mint az élesnek (a szimulátor
 * és a valóság széttartása már egyszer élesben harapott — lásd a busz kétféle sor-alakját).
 */
export const KNOWLEDGE_READ_SCOPE = 'knowledge:read';
/**
 * Azok a jogok, amelyek gyakorlása SHARED busz-ÍRÁST igényel — vagyis a service-identity nélkül
 * 503-at adnak. A csak-olvasó mód jelzését (banner) EZEKRE szűrjük: akinek egyik sincs, annak a
 * korlát nem korlát, és egy figyelmeztető sáv csak zaj a felületén.
 *
 * Ami tudatosan NINCS a listán, mert NEM a service-identityn megy:
 * - `article.view` — olvasás a felhasználó tokenjével (on-behalf),
 * - `gap.review` — a hiánynapló a derived indexből olvas,
 * - `knowledge.admin` — az index-újraépítés az Odinból a KÉRŐ tokenjével olvas.
 */
export const SERVICE_WRITE_PERMISSIONS = [
    PERMISSIONS.articleManage,
    PERMISSIONS.articlePublish,
    PERMISSIONS.articleUnpublish,
    PERMISSIONS.articleArchive,
    PERMISSIONS.articleDelete,
    PERMISSIONS.articlePublishPublic,
    PERMISSIONS.spaceManage,
    PERMISSIONS.spaceDelete,
    PERMISSIONS.categoryManage,
    PERMISSIONS.categoryDelete,
    PERMISSIONS.commentAdd,
    PERMISSIONS.commentModerate,
    PERMISSIONS.revisionRestore,
];
/** Érinti-e a csak-olvasó mód ezt a felhasználót (van-e bármilyen írás-jellegű joga). */
export function isAffectedByReadOnly(permissions) {
    return SERVICE_WRITE_PERMISSIONS.some((key) => permissions.includes(key));
}
const MEMBER = [
    KNOWLEDGE_READ_SCOPE,
    PERMISSIONS.articleView,
    PERMISSIONS.commentAdd,
];
const ADMIN = [
    ...MEMBER,
    PERMISSIONS.articleManage,
    PERMISSIONS.articlePublish,
    PERMISSIONS.articleUnpublish,
    PERMISSIONS.articleArchive,
    PERMISSIONS.articleDelete,
    PERMISSIONS.spaceManage,
    PERMISSIONS.categoryManage,
    PERMISSIONS.commentModerate,
    PERMISSIONS.revisionRestore,
    PERMISSIONS.gapReview,
];
/**
 * Tier model (member ⊂ admin ⊂ superadmin) — the role carries the defaults, per-user
 * grant/deny is the exception. Mirrors the Odin manifest roles exactly (member 3, admin 13,
 * superadmin 17 — the scope key included); used by the mock Odin and by tests, never as an
 * authorization decision (that reads the token's `permissions`).
 */
export const ROLE_PERMISSIONS = {
    member: MEMBER,
    admin: ADMIN,
    superadmin: [KNOWLEDGE_READ_SCOPE, ...ALL_PERMISSIONS],
};
/**
 * A szerep NEVE a jog-halmazból — a profil-oldal „hozzáférési szint" sorához.
 *
 * MIÉRT KELL VISSZAFEJTENI: a token a jogok LISTÁJÁT hordozza, a szerep nevét nem (az az Odinban
 * él, per-user kiosztásként). A profil viszont arra a kérdésre válaszol, hogy „mi a hozzáférési
 * szintem" — arra a nyers 17 kulcs nem felelet.
 *
 * MIÉRT KIZÁRÓLAG PONTOS EGYEZÉSRE: a katalógus szerint a szerep a defaultokat hordozza, a
 * per-user grant/deny a kivétel — vagyis a tényleges halmaz ELTÉRHET bármelyik tiertől. Egy
 * „legközelebbi tier" heurisztika ilyenkor hazudna: a felhasználó a NEVET olvassa, nem a listát,
 * és egy elvett joggal is „admin"-nak hinné magát. Ha nincs pontos egyezés, `null` — a felület
 * ilyenkor az „egyedi jogosultság-készlet" szöveget mutatja, és a lista marad az igazság.
 *
 * FIGYELEM: ez KIJELZÉS, nem jogosultsági döntés. Kaput SOHA ne kössünk rá — a jogot minden
 * végponton a BFF kényszeríti ki a token `permissions` claimjéből.
 */
export function roleFromPermissions(permissions) {
    const held = new Set(permissions);
    for (const [role, keys] of Object.entries(ROLE_PERMISSIONS)) {
        // Halmaz-egyezés mindkét irányban: a sorrend és a duplikátum nem számít, de sem plusz, sem
        // hiányzó kulcs nem fér bele.
        if (held.size === new Set(keys).size && keys.every((key) => held.has(key)))
            return role;
    }
    return null;
}
//# sourceMappingURL=permissions.js.map